// The output screen for a long-running bulk CSV job: live progress while it
// runs, then the outcome. Shared by all three bulk pages so they look and read
// identically by construction rather than by three copies staying in step.
//
// Feed it the return value of useBulkJob.
import React from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Plain Boxes, NOT <Grid item> — this component gets dropped into parents with
// different Grid setups, and `xs={12}` inside a direction="column" container
// sizes the MAIN axis (height), which threw the panel out of place on the
// coupons page. A block-level Box lays out correctly wherever it is put.
const PANEL_SX = { mt: 1, width: '100%', maxWidth: '700px' };

export default function BulkJobStatus({
    isPolling,
    progress,
    completion,
    jobMessage,
    jobFailed,
    errorFileName,
    submittedFileName,
    onDownloadErrorFile,
}) {
    return (
        <>
            {/* Live progress, inline and non-blocking. An earlier iteration used
                a modal that locked the page; that was reversed once jobs turned
                out to run 30+ minutes — an admin cannot be shut out of the
                product that long, and the job survives navigation anyway. What
                stays blocked is re-submission, not the app. */}
            {isPolling ? (
                <Box sx={PANEL_SX}>
                    <Box sx={{
                        display: 'flex', gap: 1.5, alignItems: 'flex-start',
                        p: 1.5, borderRadius: 1,
                        bgcolor: '#fff8e1',
                        border: '1px solid', borderColor: 'warning.light',
                    }}>
                        <CircularProgress size={20} sx={{ color: 'warning.main', mt: '2px' }} />
                        <Box>
                            {/* Name the file being processed. After a refresh, or
                                in another browser, this is the only thing that
                                says WHICH run this is. */}
                            <Typography sx={{ color: 'warning.dark', fontWeight: 600 }}>
                                {`Processing ${submittedFileName || 'CSV file'}… `}
                                {progress
                                    ? `${progress.success + progress.failed}`
                                      + `${progress.totalCount ? ` of ${progress.totalCount}` : ''} records`
                                      + ` — ${progress.success} succeeded, ${progress.failed} failed`
                                    : ''}
                            </Typography>
                            {/* There is no abort: the server has no cancel
                                endpoint, and "Back" only navigates. Say so
                                plainly rather than letting the user hunt for a
                                way to stop it. */}
                            <Typography variant="body2" sx={{ color: 'warning.dark', mt: 0.5 }}>
                                This cannot be cancelled — it will run until it finishes.
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'warning.dark', mt: 0.5 }}>
                                You can use other pages meanwhile.
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            ) : null}

            {/* Job finished. Its own panel rather than reusing the message
                surface, so the green success treatment can never land on an
                error. */}
            {completion ? (
                <Box sx={PANEL_SX}>
                    <Box sx={{
                        display: 'flex', gap: 1.5, alignItems: 'flex-start',
                        p: 1.5, borderRadius: 1,
                        bgcolor: '#e8f5e9',
                        border: '1px solid', borderColor: 'success.light',
                    }}>
                        <CheckCircleIcon sx={{ color: 'success.main' }} />
                        <Box>
                            <Typography sx={{ color: 'success.dark', fontWeight: 700 }}>
                                CSV file processed completely
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {`${completion.total} record${completion.total === 1 ? '' : 's'} processed`}
                                {` — ${completion.succeeded} succeeded, ${completion.failed} failed.`}
                            </Typography>
                            {errorFileName ? (
                                <Button
                                    type="button"
                                    variant="outlined"
                                    size="small"
                                    onClick={() => onDownloadErrorFile(errorFileName)}
                                    sx={{ mt: 1 }}
                                >
                                    Download error file
                                </Button>
                            ) : null}
                        </Box>
                    </Box>
                </Box>
            ) : null}

            {/* Everything else the job had to say: a failure, or the "already
                running" notice that accompanies an attached job. */}
            {jobMessage ? (
                <Box sx={PANEL_SX}>
                    <Typography sx={{ mb: 1, ml: 1, color: jobFailed ? 'error.main' : 'text.secondary' }}>
                        {String(jobMessage)}
                    </Typography>
                    {jobFailed && errorFileName ? (
                        <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            onClick={() => onDownloadErrorFile(errorFileName)}
                            sx={{ ml: 1 }}
                        >
                            Download error file
                        </Button>
                    ) : null}
                </Box>
            ) : null}
        </>
    );
}
